import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import {
  Loader2,
  AlertTriangle,
  Check,
  X,
  MapPin,
  Search,
} from 'lucide-react';
import api from '../../lib/api';

export function PendingAddressReview() {
  const [pendingReview, setPendingReview] = useState([]);
  const [loadingPending, setLoadingPending] = useState(false);
  const [fillingNA, setFillingNA] = useState(false);
  const [addressSearch, setAddressSearch] = useState('');
  const [addressCountry, setAddressCountry] = useState('Ireland');
  const [addressResults, setAddressResults] = useState([]);
  const [searchingAddress, setSearchingAddress] = useState(false);
  const [selectedCustomerForAddress, setSelectedCustomerForAddress] = useState(null);

  useEffect(() => {
    loadPendingReview();
  }, []);

  const loadPendingReview = async () => {
    setLoadingPending(true);
    try {
      const response = await api.get('/customers/pending-review');
      setPendingReview(response.data.customers || []);
    } catch {
      // Silently handle - may not exist
    } finally {
      setLoadingPending(false);
    }
  };

  const handleBulkFillNA = async () => {
    setFillingNA(true);
    try {
      const response = await api.post('/admin/bulk-fill-na');
      toast.success(`Filled NA for ${response.data.updated} customers`);
      loadPendingReview();
    } catch (error) {
      toast.error('Failed to fill NA: ' + (error.response?.data?.detail || error.message));
    } finally {
      setFillingNA(false);
    }
  };

  const handleFillNA = async (customerId) => {
    try {
      await api.post(`/admin/fill-na/${customerId}`);
      toast.success('Address filled with NA');
      setPendingReview(prev => prev.filter(c => c.id !== customerId));
      setSelectedCustomerForAddress(null);
    } catch (error) {
      toast.error('Failed: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleAddressSearch = async () => {
    if (!addressSearch.trim()) return;
    setSearchingAddress(true);
    try {
      const response = await api.get(`/admin/address-search?q=${encodeURIComponent(addressSearch)}&country=${addressCountry}`);
      setAddressResults(response.data.results || []);
      if (!response.data.results?.length) {
        toast.info('No results found. Try a different search term.');
      }
    } catch (error) {
      toast.error('Search failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSearchingAddress(false);
    }
  };

  const handleApplyAddress = async (address) => {
    if (!selectedCustomerForAddress) return;
    try {
      await api.put(`/admin/apply-address/${selectedCustomerForAddress}`, address);
      toast.success('Address applied successfully');
      setPendingReview(prev => prev.filter(c => c.id !== selectedCustomerForAddress));
      setSelectedCustomerForAddress(null);
      setAddressResults([]);
      setAddressSearch('');
    } catch (error) {
      toast.error('Failed to apply address: ' + (error.response?.data?.detail || error.message));
    }
  };

  if (pendingReview.length === 0 && !loadingPending) return null;

  return (
    <Card className="border-amber-200 bg-amber-50/30" data-testid="pending-address-review">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-amber-700 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Pending Review ({pendingReview.length} customers)
            </CardTitle>
            <CardDescription>
              These customers are missing required data. Search for addresses or fill with NA.
            </CardDescription>
          </div>
          <Button
            onClick={handleBulkFillNA}
            disabled={fillingNA}
            variant="outline"
            className="border-amber-500 text-amber-700 hover:bg-amber-100"
            data-testid="btn-bulk-fill-na"
          >
            {fillingNA ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Check className="w-4 h-4 mr-2" />
            )}
            Fill NA for All ({pendingReview.length})
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Address Search */}
        {selectedCustomerForAddress && (
          <div className="p-4 bg-white rounded-lg border space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="font-medium">Find Address</h4>
              <Button variant="ghost" size="sm" onClick={() => {
                setSelectedCustomerForAddress(null);
                setAddressResults([]);
                setAddressSearch('');
              }}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-sm text-muted-foreground">
              Searching for: <strong>{pendingReview.find(c => c.id === selectedCustomerForAddress)?.name}</strong>
            </p>
            <div>
              <p className="text-xs text-muted-foreground mb-2">Search by address (OpenStreetMap):</p>
              <div className="flex gap-2">
                <Input
                  placeholder="Enter city, street, or Eircode..."
                  value={addressSearch}
                  onChange={(e) => setAddressSearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAddressSearch()}
                  className="flex-1"
                />
                <Select value={addressCountry} onValueChange={setAddressCountry}>
                  <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Ireland">Ireland</SelectItem>
                    <SelectItem value="United Kingdom">UK</SelectItem>
                    <SelectItem value="France">France</SelectItem>
                    <SelectItem value="Germany">Germany</SelectItem>
                    <SelectItem value="Netherlands">Netherlands</SelectItem>
                    <SelectItem value="Belgium">Belgium</SelectItem>
                    <SelectItem value="Spain">Spain</SelectItem>
                    <SelectItem value="Italy">Italy</SelectItem>
                  </SelectContent>
                </Select>
                <Button onClick={handleAddressSearch} disabled={searchingAddress} variant="outline">
                  {searchingAddress ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-2">Tip: Search by city name, street, or Eircode</p>
            </div>
            {addressResults.length > 0 && (
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {addressResults.map((addr, idx) => (
                  <div
                    key={idx}
                    className="p-2 border rounded hover:bg-gray-50 cursor-pointer text-sm"
                    onClick={() => handleApplyAddress(addr)}
                  >
                    <p className="font-medium">{addr.address_line1}</p>
                    <p className="text-xs text-muted-foreground">
                      {[addr.city, addr.county, addr.postcode, addr.country].filter(Boolean).join(', ')}
                    </p>
                  </div>
                ))}
              </div>
            )}
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => handleFillNA(selectedCustomerForAddress)}>
                Fill with NA Instead
              </Button>
            </div>
          </div>
        )}

        {/* Pending Customers List */}
        <div className="space-y-2 max-h-64 overflow-y-auto">
          {pendingReview.map((customer) => (
            <div key={customer.id} className="flex items-center justify-between p-3 bg-white rounded-lg border">
              <div className="flex-1">
                <p className="font-medium">{customer.name}</p>
                <p className="text-xs text-amber-600">{customer.import_notes}</p>
                {customer.contact_email && customer.contact_email !== 'na@placeholder.local' && (
                  <p className="text-xs text-muted-foreground">{customer.contact_email}</p>
                )}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => {
                  setSelectedCustomerForAddress(customer.id);
                  setAddressSearch(customer.name + ' Ireland');
                }}>
                  <MapPin className="w-3 h-3 mr-1" /> Search
                </Button>
                <Button variant="ghost" size="sm" onClick={() => handleFillNA(customer.id)}>
                  Fill NA
                </Button>
              </div>
            </div>
          ))}
        </div>

        {loadingPending && (
          <div className="flex items-center justify-center p-4">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
